<template>
  <content-modal
    ref="drawingCanvasModal"
    class="drawing-canvas-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    :full-screen="true"
    @modal-hide-request="hideRequest"
  >
    <template #title><span></span></template>
    <template #content>
      <modal-content>
        <template #content>
          <drawing-canvas
            :background-image-url="params.imageUrl"
            :task="task"
            @is-dirty="handleCanvasDirtyState"
            @next-data="handleCanvasImage"
            @ready="resetTaskAndProcessingState"
            @error="handleCanvasError"
          />
        </template>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="handleExitAction">
            {{ t("button_drawing_cancel") }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :loading="isLoading"
            :error="isError"
            @click="handleNextAction"
          >
            <span>{{ t("next_button_label") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import DrawingCanvas from "@plugins/drawing/components/DrawingCanvas.vue";
import {
  CANVAS_ERROR,
  CANVAS_CURRENT_ACTION,
  CANVAS_TASK,
} from "@plugins/drawing/drawing.constants";
import { CroppingToolKind } from "@plugins/cropping/cropping.constant";
import { CANVAS_FILE_SETTINGS } from "@plugins/drawing/drawing.config";
import DOMUtils from "@utils/dom.utils";
import { defineAsyncComponent } from "vue";
import drawingEventBus from "@plugins/richtext/plugins/drawing/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import { useModal } from "@/plugins/modal/composables/modal-instance";

interface Props {
  params?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  params: () => ({}),
});

const { t } = useI18n();
const modal = useModal();

const closeAriaLabel = ref("Close");
const size = ref("large");
const autoHideOff = ref(true);
const visible = ref(true);
const currentAction = ref(CANVAS_CURRENT_ACTION.DEFAULT);
const task = ref(CANVAS_TASK.DEFAULT);
const processingState = ref({
  isLoading: false,
  error: false,
});
const croppingConfiguration = ref({
  kind: CroppingToolKind.cropping_tool_4.key,
});
const modalId = ref<string>("");

const isLoading = computed(() => processingState.value.isLoading);
const isError = computed(() => processingState.value.error);

const setProcessingStateLoading = () => {
  processingState.value.isLoading = true;
};

const setProcessingStateError = () => {
  processingState.value.isLoading = false;
  processingState.value.error = true;
  // short delay before removing the error state.
  setTimeout(() => {
    resetProcessingState();
  }, 3000);
};

const resetProcessingState = () => {
  processingState.value.isLoading = false;
  processingState.value.error = false;
};

/**
 * Set task to `checkDirty` and pass it to drawing canvas as props.
 *
 * Expecting drawing canvas to emit its dirty state.
 */
const doCheckDirty = () => {
  task.value = CANVAS_TASK.CHECK_DIRTY;
};

/**
 * Set task to `next` and pass it to drawing canvas as props.
 *
 * Expecting drawing canvas to emit its image file.
 */
const doNext = () => {
  task.value = CANVAS_TASK.NEXT;
};

/**
 * Set task to `reload` and pass it to drawing canvas as props.
 *
 * Expecting drawing canvas to reload its canvas object.
 */
const doReload = () => {
  task.value = CANVAS_TASK.RELOAD;
};

/**
 * Set task to empty string.
 */
const resetTask = () => {
  task.value = CANVAS_TASK.DEFAULT;
};

const handleNextAction = () => {
  if (isLoading.value) return; // do nothing when clicking on upload button while still uploading
  currentAction.value = CANVAS_CURRENT_ACTION.NEXT;
  setProcessingStateLoading();
  doCheckDirty();
};

const handleExitAction = () => {
  currentAction.value = CANVAS_CURRENT_ACTION.EXIT;
  setProcessingStateLoading();
  doCheckDirty();
};

/**
 * Determine the next step based on drawing canvas dirty state, clear state, and current action.
 * @param isDirty drawing canvas is dirty - has modification
 * @param isClear drawing canvas is clear - has no object (undo / delete / clear all)
 */
const handleCanvasDirtyState = ({ isDirty, isClear }) => {
  if (!isDirty || (isDirty && isClear)) {
    // close modal (for both case of save and exit)
    // not dirty OR dirty but canvas has no object
    hide();
    return;
  }

  if (currentAction.value === CANVAS_CURRENT_ACTION.NEXT) {
    // proceed to next step, validate
    doNext();
  } else if (currentAction.value === CANVAS_CURRENT_ACTION.EXIT) {
    resetTask();
    promptPageDirty();
  }
};

/**
 * Process drawing canvas image file and send to cropping tool
 * @param imageFile drawing canvas image file
 */
const handleCanvasImage = async (imageFile) => {
  const uuid = props.params.uuid ? props.params.uuid : null;
  // Set file name and ensure file extension is set to the correct value.
  const fileName =
    props.params.fileName && typeof props.params.fileName === "string"
      ? props.params.fileName.replace(/\.[^/.]+$/, CANVAS_FILE_SETTINGS.defaultFileExtension)
      : CANVAS_FILE_SETTINGS.defaultFileName;

  // return the drawn image file.
  let drawingFile = new File([imageFile], fileName, {
    type: CANVAS_FILE_SETTINGS.defaultFileType,
  });

  if (props.params.skipCropping) {
    // Apply white background when skipping cropping
    drawingFile = await applyWhiteBackground(drawingFile);
    handleCropped({ file: drawingFile }, uuid);
    return;
  }

  const CroppingModal = defineAsyncComponent(
    () => import("@plugins/cropping/components/CroppingCanvasModal.vue"),
  );
  modalId.value = modal.show(
    CroppingModal,
    {
      file: drawingFile,
      croppingConfiguration: croppingConfiguration.value,
      secondaryBtnLabel: t("button_back"),
    },
    {
      cropped: (data) => handleCropped(data, uuid),
      hide: () => doReload(),
    },
    true,
  );
};

/**
 * Drawing canvas encountered error.
 * @param errorCode drawing canvas error code
 */
const handleCanvasError = (errorCode: string) => {
  if (errorCode === CANVAS_ERROR.BACKGROUND_LOAD_FAILED) {
    alert("We have encountered an unexpected error. Try again later.");
    hide();
  }
};

const handleCropped = (data, uuid) => {
  drawingEventBus.$emit("commitDrawing", {
    file: data.file,
    uuid: uuid,
    createAsNew: !!props.params.createAsNew,
  });
  modal.remove(modalId.value);
};

const resetTaskAndProcessingState = () => {
  resetTask();
  resetProcessingState();
};

/**
 * Apply white background to image file
 * @param file image file
 * @returns file with white background
 */
const applyWhiteBackground = async (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      // Revoke object URL to free memory
      URL.revokeObjectURL(objectUrl);

      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        resolve(file);
        return;
      }

      // Fill with white background
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw image on top
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(file);
          return;
        }

        const newFile = new File([blob], file.name, {
          type: file.type,
        });
        resolve(newFile);
      }, file.type);
    };

    img.src = objectUrl;
  });
};

const promptPageDirty = () => {
  if (confirm(t("dirty_confirmation_prompt"))) {
    // discard changes, close modal
    hide();
  } else {
    // don't exit, continue edit
    currentAction.value = CANVAS_CURRENT_ACTION.DEFAULT;
    resetProcessingState();
  }
};

const hide = () => {
  // restore previous scrollability
  drawingEventBus.$emit("hide", undefined);
  EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
  DOMUtils.removeClass("body", "bx--body--with-modal-open");
  modal.remove(props.params.modalId);
};

const hideRequest = (event: object) => {
  // to prevent close modal upon clicking anywhere outside of the modal container
  if (event["cv:reason"] !== "external-click") {
    handleExitAction();
  }
};

onBeforeUnmount(() => {
  hide();
});

defineExpose({
  setProcessingStateError,
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/spacing";

.fullscreen.bx--modal.content-modal.drawing-canvas-modal {
  .bx--modal-header {
    display: none;
  }

  .bx--modal-container {
    @include media("tablet-min") {
      max-width: 1333px;

      // fallback to 100vh for browsers that do not support 100dvh
      height: calc(100vh - #{$layout-05});
      max-height: calc(100vh - #{$layout-05});

      // use 100dvh instead of 100vh to avoid issues with mobile browsers' dynamic viewport height
      height: calc(100dvh - #{$layout-05});
      max-height: calc(100dvh - #{$layout-05});
    }
  }

  .bx--modal-content {
    position: unset;
    width: 100%;
    overflow: hidden;

    > .wrapper {
      height: 100%;

      > .content {
        height: 100%;

        > .drawing-canvas {
          height: 100%;
        }
      }
    }

    @include media("mobile") {
      margin-top: 7rem;
      padding-top: 0;
    }

    @include media("tablet-min") {
      padding-left: 0;
      padding-top: $spacing-05;
    }
  }
}
</style>
